/**
 * Settings → AI Agents — Multi-Agent / Digital Employees Management.
 * Manage your team of specialized AI Digital Employees.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Bot,
  Brain,
  CheckCircle2,
  ChevronRight,
  Copy,
  Database,
  Eye,
  FileText,
  Globe,
  Key,
  Layers,
  Loader2,
  Lock,
  Mail,
  MessageCircle,
  MessageSquare,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  Shield,
  ShieldAlert,
  Sparkles,
  Sliders,
  Send,
  Trash2,
  User,
  Users,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { agentsApi, brainApi, connectionsApi } from "@/lib/api";
import { INBOX_CHANNEL_REGISTRY } from "@/lib/inboxChannels";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/store/useAppStore";
import { Shell } from "@/pages/settings/SettingsPages";

const WIZARD_STEPS = [
  "Identity",
  "Instructions",
  "Channels",
  "Knowledge",
  "Skills",
  "Guardrails",
  "Memory & Model",
  "Permissions",
  "Review",
];





const DEFAULT_SKILLS = [
  { key: "lead_research", label: "Lead Research", desc: "Find and research prospect information across web sources" },
  { key: "web_search", label: "Web Research", desc: "Search live web for company details and market intelligence" },
  { key: "email_outreach", label: "Email Outreach", desc: "Draft and send personalized email messages via connected accounts" },
  { key: "whatsapp_outreach", label: "WhatsApp Outreach", desc: "Engage prospects and customers over connected WhatsApp numbers" },
  { key: "telegram_outreach", label: "Telegram Outreach", desc: "Manage Telegram channel and direct customer communications" },
  { key: "crm_lead_creation", label: "CRM Lead Creation", desc: "Create and update qualified leads and deals in CRM" },
  { key: "lead_qualification", label: "Lead Qualification", desc: "Score prospects based on qualification criteria and signals" },
  { key: "customer_support", label: "Customer Support", desc: "Answer inbound inquiries using knowledge base facts" },
  { key: "knowledge_search", label: "Knowledge Search", desc: "Query assigned Brain knowledge documents and excerpts" },
  { key: "ticket_creation", label: "Ticket Creation", desc: "Generate support tickets for complex customer requests" },
  { key: "escalation", label: "Human Escalation", desc: "Escalate urgent or unhandled issues to human teammates" },
];





/** Map a connection's `kind` field to a human-readable channel label. */
const _kindLabelMap = Object.fromEntries(
  INBOX_CHANNEL_REGISTRY.filter((d) => d.connectionKind).map((d) => [d.connectionKind, d.label]),
);
function channelKindLabel(kind) {
  return _kindLabelMap[kind] || (kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : "");
}

const EMPTY_DRAFT = {
  name: "",
  description: "",
  color: "from-indigo-500 to-violet-600",
  status: "active",
  primary_responsibility: "",
  system_prompt: "",
  channels: [],
  knowledge_source_ids: [],
  skills: ["lead_research", "email_outreach", "whatsapp_outreach", "crm_lead_creation", "knowledge_search"],
  guardrails: {
    require_draft_approval: true,
    working_hours_only: false,
    pii_redaction: true,
    escalation_on_redflag: true,
    max_daily_outreach: 50,
    prohibited_topics: "Do not discuss unauthorized product lines or financial commitments.",
  },
  model_provider: "OpenAI",
  model: "gpt-4o",
  temperature: 0.4,
  max_tokens: 2048,
  memory_settings: {
    isolated_workspace: true,
    retention_days: 90,
  },
  permissions: [],
  signal_assignments: [],
};

function statusBadge(status) {
  const map = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    disabled: "bg-amber-50 text-amber-700 border-amber-200/80",
    archived: "bg-rose-50 text-rose-700 border-rose-200/80",
  };
  return map[status] || map.draft;
}

function toggleListValue(list, value) {
  const set = new Set(list || []);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  return Array.from(set);
}

export function SettingsAiAgents() {
  const { auth } = useAppStore();
  const role = auth?.server?.role || "owner";
  const canManage = role === "owner" || role === "admin";

  const [agents, setAgents] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [sources, setSources] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const wizardStepButtonRefs = useRef([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Detail Modal state
  const [detailAgent, setDetailAgent] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityTimeframe, setActivityTimeframe] = useState("30");
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Test Drawer state
  const [testAgent, setTestAgent] = useState(null);
  const [testInput, setTestInput] = useState("");
  const [testMessages, setTestMessages] = useState([]);
  const [testing, setTesting] = useState(false);

  // Delete Confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cat, brain, connRes] = await Promise.all([
        agentsApi.list({ q: q || undefined, include_archived: true }),
        agentsApi.catalog(),
        brainApi.listCustomSources(),
        connectionsApi.list().catch(() => ({ connections: [] })),
      ]);
      setAgents(list.agents || []);
      setCatalog(cat || {});
      setSources(brain.custom_sources || []);
      setConnections(connRes.connections || connRes.rows || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load Digital Employees");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!wizardOpen) return;
    const activeStepButton = wizardStepButtonRefs.current[wizardStep];
    activeStepButton?.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
  }, [wizardStep, wizardOpen]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return agents;
    return agents.filter((a) =>
      [a.name, a.description, a.login_email]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [agents, q]);

  const activeCount = useMemo(
    () => agents.filter((a) => a.status === "active").length,
    [agents],
  );

  function openCreate() {
    setEditId(null);
    setDraft({
      ...EMPTY_DRAFT,
      permissions: [],
    });
    setWizardStep(0);
    setWizardOpen(true);
  }

  async function openEdit(agent) {
    try {
      const full = await agentsApi.get(agent.id);
      setEditId(agent.id);
      const assignedConnIds = (full.channels || []).map((c) => c.id);
      setDraft({
        name: full.name || "",
        description: full.description || "",
        color: full.color || "from-indigo-500 to-violet-600",
        status: full.status || "active",
        primary_responsibility: full.description || "",
        system_prompt: full.system_prompt || "",
        channels: assignedConnIds,
        knowledge_source_ids: (full.knowledge || []).map((k) => k.source_id),
        skills: (full.skills || []).map((s) => s.skill_key),
        guardrails: { ...EMPTY_DRAFT.guardrails, ...(full.guardrails || {}) },
        model_provider: "OpenAI",
        model: full.model || "gpt-4o",
        temperature: full.temperature ?? 0.4,
        max_tokens: full.max_tokens ?? 2048,
        memory_settings: full.memory_settings || { isolated_workspace: true, retention_days: 90 },
        permissions: (full.permissions || []).map((p) => ({
          permission_id: p.permission_id || p.id,
          id: p.permission_id || p.id,
          key: p.key,
        })),
        signal_assignments: (full.signal_assignments || []).map((s) => ({
          signal_kind: s.signal_kind,
          resource_id: s.resource_id || null,
        })),
      });
      setWizardStep(0);
      setWizardOpen(true);
    } catch (err) {
      toast.error(err?.message || "Failed to load agent details");
    }
  }

  async function openDetail(agent) {
    try {
      const full = await agentsApi.get(agent.id);
      setDetailAgent(full);
      setDetailTab("overview");
      loadActivity(agent.id);
    } catch (err) {
      toast.error(err?.message || "Failed to load agent details");
    }
  }

  async function loadActivity(agentId) {
    setLoadingActivity(true);
    try {
      const res = await agentsApi.activity(agentId, { limit: 50 });
      setActivityLogs(res.activity || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load activity logs");
    } finally {
      setLoadingActivity(false);
    }
  }

  const isNameTaken = useMemo(() => {
    const trimmed = (draft.name || "").trim().toLowerCase();
    if (!trimmed) return false;
    return (agents || []).some(
      (a) =>
        a.status !== "archived" &&
        a.id !== editId &&
        a.name.trim().toLowerCase() === trimmed
    );
  }, [agents, draft.name, editId]);

  async function validateStepAsync(step) {
    if (step === 0) {
      const name = draft.name.trim();
      if (!name) {
        toast.error("Agent name is required");
        return false;
      }
      if (name.length < 3) {
        toast.error("Agent name must be at least 3 characters");
        return false;
      }
      if (name.length > 50) {
        toast.error("Agent name must be at most 50 characters");
        return false;
      }
      if (isNameTaken) {
        toast.error(`An AI agent named "${name}" already exists in this workspace.`);
        return false;
      }
      try {
        const check = await agentsApi.checkName(name, editId);
        if (!check.available) {
          toast.error(check.reason || `An AI agent named "${name}" already exists.`);
          return false;
        }
      } catch (err) {
        // If API check fails, local validation was already executed
      }
    }
    if (step === 1 && !draft.system_prompt.trim()) {
      toast.error("System instructions are required for the AI employee");
      return false;
    }
    if (step === 3 && (!draft.knowledge_source_ids || draft.knowledge_source_ids.length === 0)) {
      toast.error("At least one Knowledge Base is required");
      return false;
    }
    return true;
  }

  async function saveWizard() {
    for (let i = 0; i < WIZARD_STEPS.length - 1; i += 1) {
      if (!(await validateStepAsync(i))) {
        setWizardStep(i);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description || draft.primary_responsibility || null,
        status: draft.status || "active",
        system_prompt: draft.system_prompt || "",
        model: draft.model || "gpt-4o",
        temperature: draft.temperature,
        max_tokens: draft.max_tokens,
        channels: draft.channels || [],
        knowledge_source_ids: draft.knowledge_source_ids || [],
        skills: draft.skills || [],
        guardrails: draft.guardrails || {},
        permissions: draft.permissions || [],
        memory_settings: draft.memory_settings || {},
        signal_assignments: draft.signal_assignments || [],
      };
      if (editId) {
        await agentsApi.update(editId, payload);
        toast.success("Digital Employee updated successfully");
      } else {
        await agentsApi.create(payload);
        toast.success("Digital Employee created successfully");
      }
      setWizardOpen(false);
      refresh();
    } catch (err) {
      const msg = err?.message || "Save failed";
      if (msg.includes("already exists") || err?.code === "name_conflict") {
        setWizardStep(0);
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function setStatus(agent, status) {
    try {
      await agentsApi.setStatus(agent.id, status);
      toast.success(`Agent "${agent.name}" is now ${status}`);
      refresh();
      if (detailAgent && detailAgent.id === agent.id) {
        setDetailAgent({ ...detailAgent, status });
      }
    } catch (err) {
      toast.error(err?.message || "Status update failed");
    }
  }

  async function handleDuplicate(agent) {
    try {
      await agentsApi.duplicate(agent.id);
      toast.success(`Duplicated agent "${agent.name}"`);
      refresh();
    } catch (err) {
      toast.error(err?.message || "Duplicate failed");
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    if (deleteTarget.is_default) {
      toast.error("Default system agent cannot be deleted.");
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      await agentsApi.remove(deleteTarget.id);
      toast.success(`Digital Employee "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      if (detailAgent && detailAgent.id === deleteTarget.id) setDetailAgent(null);
      refresh();
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  async function runTestMessage() {
    if (!testInput.trim() || !testAgent || testing) return;
    const userMsg = { role: "user", content: testInput.trim(), time: new Date().toLocaleTimeString() };
    setTestMessages((prev) => [...prev, userMsg]);
    const currentInput = testInput.trim();
    setTestInput("");
    setTesting(true);

    try {
      const res = await agentsApi.test(testAgent.id, currentInput);
      if (res?.ok === false) {
        throw new Error(res?.error || "Agent test failed");
      }
      const replyText = (
        res?.text
        || res?.reply
        || res?.response
        || res?.body_text
        || ""
      ).trim();
      const botMsg = {
        role: "assistant",
        content: replyText || "No response returned from agent.",
        time: new Date().toLocaleTimeString(),
        trace: res?.telemetry || res?.trace_id || res?.transport,
      };
      setTestMessages((prev) => [...prev, botMsg]);
    } catch (err) {
      setTestMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error testing agent: ${err?.message || "Execution error"}`,
          time: new Date().toLocaleTimeString(),
          isError: true,
        },
      ]);
    } finally {
      setTesting(false);
    }
  }

  if (!canManage) {
    return (
      <Shell title="AI Agents" desc="Digital Employee management is available to workspace admins.">
        <div className="p-4 sm:p-6 max-w-5xl">
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 text-[13px] text-amber-900">
            You do not have permission to manage workspace Digital Employees. Contact your workspace administrator.
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      title="AI Agents"
      desc="Manage your specialized AI workforce of Digital Employees."
    >
      <div className="p-4 sm:p-6 max-w-6xl space-y-6 sm:space-y-7 min-w-0" data-testid="ai-agents-page">

        {/* Hero Header */}
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-indigo-50/50 via-white to-slate-50/40 p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-indigo-700 font-bold inline-flex items-center gap-1.5">
                <Sparkles size={13} /> Digital Employees Workforce
              </div>
              <h2 className="text-[20px] sm:text-[22px] font-bold tracking-tight text-slate-900 mt-1">
                My AI Team
              </h2>
              <p className="text-[12.5px] text-slate-600 mt-0.5 max-w-xl">
                One founder account, multiple specialized AI agents. Assign distinct identities, communication channels, knowledge bases, skills, and guardrails to each employee.
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
              <div className="rounded-xl bg-white ring-1 ring-slate-200/80 px-4 py-2.5 shadow-2xs flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-sm">
                  {activeCount}
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-slate-900">{activeCount} / {agents.length} Active</div>
                  <div className="text-[10.5px] text-slate-500">Digital Employees</div>
                </div>
              </div>

              <Button size="sm" className="h-10 px-4 text-[12.5px] font-semibold bg-indigo-600 hover:bg-indigo-700 shadow-sm" onClick={openCreate} data-testid="ai-agents-create">
                <Plus size={16} className="mr-1.5" /> Create AI Agent
              </Button>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-200/60 flex items-center gap-4 flex-wrap text-[11.5px] text-slate-600">
            <div className="inline-flex items-center gap-1.5">
              <Shield size={13} className="text-indigo-600" />
              <span>Strict Knowledge &amp; Channel Isolation</span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <Lock size={13} className="text-emerald-600" />
              <span>Isolated Memory Workspaces</span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <Users size={13} className="text-amber-600" />
              <span>Single Founder Control Plane</span>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
            <Input
              className="pl-9 h-9 text-[12.5px] bg-white border-slate-200"
              placeholder="Search by employee name, role, or description…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="ai-agents-search"
            />
          </div>
        </div>

        {/* Digital Employee Cards Grid */}
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="ai-agents-loading">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-slate-100" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-4 w-24 bg-slate-100 rounded" />
                    <div className="h-3 w-32 bg-slate-100 rounded" />
                  </div>
                </div>
                <div className="h-12 bg-slate-100 rounded" />
              </div>
            ))}
          </div>
        ) : !filtered.length ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-8 sm:p-12 text-center" data-testid="agents-empty-state">
            <div className="h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200 flex items-center justify-center mx-auto mb-3">
              <Bot size={24} />
            </div>
            <div className="text-[16px] font-bold text-slate-900">No Digital Employees yet</div>
            <p className="text-[12.5px] text-slate-500 mt-1 mb-4 max-w-md mx-auto leading-relaxed">
              Build your AI workforce. Create specialized agents for Product Outreach, Customer Support, Lead Research, and CRM automation.
            </p>
            <Button size="sm" onClick={openCreate} className="h-9 text-[12.5px]">
              <Plus size={14} className="mr-1.5" /> Create AI Agent
            </Button>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="ai-agents-list">
            {filtered.map((agent) => {
              const skillCount = (agent.skills || []).length;
              const knowledgeCount = (agent.knowledge || []).length;
              const channelList = agent.channels || [];

              return (
                <div
                  key={agent.id}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between"
                  data-testid={`agent-card-${agent.id}`}
                >
                  <div>
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[15px] font-bold text-slate-900 truncate">
                          {agent.name}
                        </div>
                      </div>
                      <Badge className={cn("border text-[10.5px] font-semibold shrink-0 capitalize", statusBadge(agent.status))}>
                        {agent.status}
                      </Badge>
                    </div>

                    {/* Responsibility & Description */}
                    <p className="text-[12px] text-slate-600 mt-3.5 line-clamp-2 min-h-[36px] leading-relaxed">
                      {agent.description || agent.system_prompt || "No description provided."}
                    </p>

                    {/* Assigned Channels */}
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
                        Assigned Channels
                      </div>
                      {channelList.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {channelList.map((ch) => (
                            <span key={ch.id || ch.kind} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                              {ch.kind === "whatsapp" && <MessageCircle size={10} className="text-emerald-600" />}
                              {ch.kind === "gmail" && <Mail size={10} className="text-rose-600" />}
                              {ch.kind === "telegram" && <Send size={10} className="text-sky-600" />}
                              <span>{channelKindLabel(ch.kind)}</span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-[11px] text-slate-400 italic">No channel accounts connected</div>
                      )}
                    </div>

                    {/* Resource Counts */}
                    <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-600">
                      <div className="inline-flex items-center gap-1">
                        <Brain size={12} className="text-indigo-600" />
                        <span className="font-semibold">{knowledgeCount}</span> KB sources
                      </div>
                      <div className="inline-flex items-center gap-1">
                        <Zap size={12} className="text-amber-500" />
                        <span className="font-semibold">{skillCount}</span> skills
                      </div>
                    </div>
                  </div>

                  {/* Actions Footer */}
                  <div className="mt-5 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-1">
                    <Button size="sm" variant="outline" className="h-8 text-[11.5px] px-2.5" onClick={() => openDetail(agent)} data-testid={`agent-view-${agent.id}`}>
                      <Eye size={13} className="mr-1 text-slate-500" /> View
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-[11.5px] px-2.5" onClick={() => openEdit(agent)} data-testid={`agent-edit-${agent.id}`}>
                      <Pencil size={13} className="mr-1 text-slate-500" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-[11.5px] px-2.5 text-slate-600 hover:text-indigo-600" title="Test Agent" onClick={() => { setTestAgent(agent); setTestMessages([]); }}>
                      <Bot size={13} className="mr-1 text-indigo-600" /> Test
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-[11.5px] px-2.5 text-slate-600 hover:text-indigo-600"
                      title="Duplicate Digital Employee"
                      onClick={() => handleDuplicate(agent)}
                    >
                      <Copy size={13} className="mr-1 text-slate-500" /> Duplicate
                    </Button>
                    {agent.status === "active" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-[11.5px] px-2.5 text-slate-600 hover:text-amber-600"
                        title="Deactivate agent"
                        onClick={() => setStatus(agent, "disabled")}
                      >
                        <PowerOff size={13} className="mr-1 text-slate-500" /> Deactivate
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-[11.5px] px-2.5 text-slate-600 hover:text-emerald-600"
                        title="Activate agent"
                        onClick={() => setStatus(agent, "active")}
                      >
                        <Power size={13} className="mr-1 text-slate-500" /> Activate
                      </Button>
                    )}
                    {!agent.is_default && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-[11.5px] px-2.5 text-slate-600 hover:text-rose-600"
                        title="Delete agent"
                        onClick={() => setDeleteTarget(agent)}
                      >
                        <Trash2 size={13} className="mr-1 text-slate-500" /> Delete
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ========================================================================= */}
        {/* 9-STEP AGENT CREATION / EDITING WIZARD DIALOG */}
        {/* ========================================================================= */}
        <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
          <DialogContent
            className="w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto p-5 sm:p-6 text-slate-900 shadow-xl border-slate-200"
            preventOutsideDismiss
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader className="pb-2 border-b border-slate-100">
              <DialogTitle className="text-[18px] font-bold text-slate-900">
                {editId ? "Edit Digital Employee" : "Create Digital Employee"}
              </DialogTitle>
              <div className="text-[12px] text-slate-500 mt-0.5">
                Step {wizardStep + 1} of {WIZARD_STEPS.length} — <span className="font-bold text-slate-800">{WIZARD_STEPS[wizardStep]}</span>
              </div>
            </DialogHeader>

            {/* Stepper Progress Bar */}
            <div className="flex items-center gap-1 overflow-x-auto py-2.5 scrollbar-thin border-b border-slate-100">
              {WIZARD_STEPS.map((label, idx) => {
                const active = wizardStep === idx;
                const completed = wizardStep > idx;
                return (
                  <button
                    key={label}
                    type="button"
                    ref={(el) => {
                      wizardStepButtonRefs.current[idx] = el;
                    }}
                    onClick={async () => {
                      if (idx < wizardStep || (await validateStepAsync(wizardStep))) {
                        setWizardStep(idx);
                      }
                    }}
                    className={cn(
                      "px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors whitespace-nowrap flex items-center gap-1 shrink-0",
                      active
                        ? "bg-indigo-600 text-white shadow-xs"
                        : completed
                        ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200",
                    )}
                  >
                    <span>{idx + 1}. {label}</span>
                    {completed && <CheckCircle2 size={11} className="text-indigo-600" />}
                  </button>
                );
              })}
            </div>

            {/* Step Content Area */}
            <div className="py-4 min-h-[340px]">

              {/* STEP 1: IDENTITY */}
              {wizardStep === 0 && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[12.5px] font-bold text-slate-800">Agent Name <span className="text-rose-500">*</span></Label>
                    <Input
                      className={cn("mt-1 h-9 text-[12.5px]", isNameTaken && "border-rose-400 focus-visible:ring-rose-400")}
                      value={draft.name}
                      placeholder="e.g. Outreach Agent"
                      maxLength={50}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    />
                    {isNameTaken && (
                      <p className="mt-1 text-[11.5px] font-medium text-rose-600">
                        An AI agent named &quot;{draft.name.trim()}&quot; already exists in this workspace.
                      </p>
                    )}
                  </div>





                  <div>
                    <Label className="text-[12.5px] font-bold text-slate-800">Description</Label>
                    <Textarea
                      className="mt-1 text-[12.5px]"
                      value={draft.description}
                      onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      placeholder="Responsible for finding and engaging prospects for Product 1…"
                      rows={2}
                    />
                  </div>
                </div>
              )}

              {/* STEP 2: RESPONSIBILITY & INSTRUCTIONS */}
              {wizardStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[12.5px] font-bold text-slate-800">Primary Responsibility</Label>
                    <Input
                      className="mt-1 h-9 text-[12.5px]"
                      value={draft.primary_responsibility}
                      onChange={(e) => setDraft({ ...draft, primary_responsibility: e.target.value })}
                      placeholder="e.g. You are responsible for outbound prospecting and lead qualification for Product 1."
                    />
                  </div>

                  <div>
                    <Label className="text-[12.5px] font-bold text-slate-800">System Instructions / System Prompt <span className="text-rose-500">*</span></Label>
                    <Textarea
                      className="mt-1 font-mono text-[12px] leading-relaxed"
                      value={draft.system_prompt}
                      onChange={(e) => setDraft({ ...draft, system_prompt: e.target.value })}
                      placeholder={`You are the Product 1 Outreach Agent.
Your responsibility is to identify potential customers, research prospects, qualify leads, and prepare personalized outreach.
You must only discuss Product 1.
You must not provide information about Product 2 unless explicitly authorized.`}
                      rows={8}
                    />
                    <div className="text-[11px] text-slate-500 mt-1">
                      These system instructions dictate the agent's behavior and domain boundaries. They remain isolated to this specific Digital Employee.
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: SEPARATE COMMUNICATION CHANNELS */}
              {wizardStep === 2 && (
                <div className="space-y-3">
                  <div className="rounded-xl bg-indigo-50/60 ring-1 ring-indigo-200/70 p-3 text-[12px] text-indigo-900 leading-relaxed">
                    <span className="font-bold">Channel Connection Isolation:</span> Select the communication accounts owned by this employee. Inbound messages from these accounts will route specifically to this agent.
                  </div>

                  {!connections.filter((c) => c.configured).length ? (
                    <div className="text-[12.5px] text-slate-500 p-6 border border-dashed rounded-xl text-center">
                      No connected channel accounts found. Connect WhatsApp, Gmail, or Telegram in Settings → Connections.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                      {connections.filter((c) => c.configured).map((c) => {
                        const checked = (draft.channels || []).includes(c.id);
                        return (
                          <label
                            key={c.id}
                            className={cn(
                              "flex items-start justify-between gap-3 rounded-xl border p-3 cursor-pointer transition-colors text-[12.5px] min-w-0 w-full",
                              checked ? "border-indigo-300 bg-indigo-50/40 ring-1 ring-indigo-200" : "border-slate-200 hover:bg-slate-50",
                            )}
                          >
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <input
                                type="checkbox"
                                className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                                checked={checked}
                                onChange={() => setDraft({
                                  ...draft,
                                  channels: toggleListValue(draft.channels, c.id),
                                })}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-slate-900 capitalize flex items-center gap-1.5 flex-wrap break-words">
                                  {c.kind === "whatsapp" && <MessageCircle size={14} className="text-emerald-600 shrink-0" />}
                                  {c.kind === "gmail" && <Mail size={14} className="text-rose-600 shrink-0" />}
                                  {c.kind === "telegram" && <Send size={14} className="text-sky-600 shrink-0" />}
                                  <span>{c.kind}</span>
                                </div>
                                <div className="text-[11px] text-slate-500 font-mono mt-0.5 break-all whitespace-normal">{c.account_identifier || "Default Account"}</div>
                              </div>
                            </div>
                            <Badge className={cn("text-[10px] shrink-0 self-start mt-0.5", c.status === "connected" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600")}>
                              {c.status}
                            </Badge>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* STEP 4: SEPARATE KNOWLEDGE BASE */}
              {wizardStep === 3 && (
                <div className="space-y-3">
                  <div className="rounded-xl bg-indigo-50/60 ring-1 ring-indigo-200/70 p-3 text-[12px] text-indigo-900 leading-relaxed">
                    <span className="font-bold">Knowledge Isolation:</span> Assign specific Brain knowledge sources to this agent. Information retrieval will strictly isolate content to assigned knowledge bases.
                  </div>

                  {!sources.length ? (
                    <div className="text-[12.5px] text-slate-500 p-6 border border-dashed rounded-xl text-center">
                      No custom knowledge sources indexed yet. Add PDF, DOCX, or text sources in Sellyx Brain.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                      {sources.map((s) => {
                        const checked = (draft.knowledge_source_ids || []).includes(s.id);
                        return (
                          <label
                            key={s.id}
                            className={cn(
                              "flex items-start justify-between gap-3 rounded-xl border p-3 cursor-pointer transition-colors text-[12.5px] min-w-0 w-full",
                              checked ? "border-indigo-300 bg-indigo-50/40 ring-1 ring-indigo-200" : "border-slate-200 hover:bg-slate-50",
                            )}
                          >
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <input
                                type="checkbox"
                                className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                                checked={checked}
                                onChange={() => setDraft({
                                  ...draft,
                                  knowledge_source_ids: toggleListValue(draft.knowledge_source_ids, s.id),
                                })}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-slate-900 break-words whitespace-normal leading-snug" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                                  {s.name || s.id}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5 break-words whitespace-normal">
                                  {s.source_type || "Document Source"}
                                </div>
                              </div>
                            </div>
                            <Badge className="bg-slate-100 text-slate-700 text-[10px] shrink-0 self-start mt-0.5">
                              Agent KB
                            </Badge>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* STEP 5: AGENT SKILLS */}
              {wizardStep === 4 && (
                <div className="space-y-3">
                  <p className="text-[12px] text-slate-500">Select the execution skills permitted for this Digital Employee.</p>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                    {DEFAULT_SKILLS.map((s) => {
                      const checked = (draft.skills || []).includes(s.key);
                      return (
                        <label
                          key={s.key}
                          className={cn(
                            "flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors text-[12.5px]",
                            checked ? "border-indigo-300 bg-indigo-50/40 ring-1 ring-indigo-200" : "border-slate-200 hover:bg-slate-50",
                          )}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            checked={checked}
                            onChange={() => setDraft({
                              ...draft,
                              skills: toggleListValue(draft.skills, s.key),
                            })}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-900">{s.label}</div>
                            <div className="text-[11.5px] text-slate-500 mt-0.5">{s.desc}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* STEP 6: GUARDRAILS */}
              {wizardStep === 5 && (
                <div className="space-y-4">
                  <p className="text-[12px] text-slate-500">Define what the agent can do, cannot do, and must escalate.</p>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
                      <div>
                        <div className="text-[13px] font-semibold text-slate-900">Require Approval Before Sending</div>
                        <div className="text-[11.5px] text-slate-500 mt-0.5">Agent drafts outreach for review instead of sending directly</div>
                      </div>
                      <Switch
                        checked={Boolean(draft.guardrails?.require_draft_approval)}
                        onCheckedChange={(v) => setDraft({
                          ...draft,
                          guardrails: { ...draft.guardrails, require_draft_approval: v },
                        })}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
                      <div>
                        <div className="text-[13px] font-semibold text-slate-900">Working Hours Only</div>
                        <div className="text-[11.5px] text-slate-500 mt-0.5">Execute outbound communications only during business hours</div>
                      </div>
                      <Switch
                        checked={Boolean(draft.guardrails?.working_hours_only)}
                        onCheckedChange={(v) => setDraft({
                          ...draft,
                          guardrails: { ...draft.guardrails, working_hours_only: v },
                        })}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
                      <div>
                        <div className="text-[13px] font-semibold text-slate-900">PII &amp; Sensitive Data Redaction</div>
                        <div className="text-[11.5px] text-slate-500 mt-0.5">Filter sensitive financial data and credentials from agent prompts</div>
                      </div>
                      <Switch
                        checked={Boolean(draft.guardrails?.pii_redaction)}
                        onCheckedChange={(v) => setDraft({
                          ...draft,
                          guardrails: { ...draft.guardrails, pii_redaction: v },
                        })}
                      />
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-3">
                      <Label className="text-[13px] font-semibold text-slate-900">Max Daily Outreach Messages</Label>
                      <Input
                        type="number"
                        className="mt-1.5 h-9 text-[12.5px]"
                        value={draft.guardrails?.max_daily_outreach ?? 50}
                        onChange={(e) => setDraft({
                          ...draft,
                          guardrails: {
                            ...draft.guardrails,
                            max_daily_outreach: Number(e.target.value || 0),
                          },
                        })}
                      />
                    </div>

                    <div>
                      <Label className="text-[12.5px] font-semibold text-slate-800">Prohibited Topics &amp; Rules</Label>
                      <Textarea
                        className="mt-1 text-[12.5px]"
                        value={draft.guardrails?.prohibited_topics || ""}
                        onChange={(e) => setDraft({
                          ...draft,
                          guardrails: { ...draft.guardrails, prohibited_topics: e.target.value },
                        })}
                        placeholder="✕ Cannot discuss Product 2&#10;✕ Cannot make financial commitments&#10;✕ Cannot delete CRM records"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 7: MEMORY & MODEL */}
              {wizardStep === 6 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[12.5px] font-bold text-slate-800">Model Provider</Label>
                      <Select value={draft.model_provider} onValueChange={(v) => setDraft({ ...draft, model_provider: v })}>
                        <SelectTrigger className="mt-1 h-9 text-[12.5px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OpenAI">OpenAI</SelectItem>
                          <SelectItem value="Anthropic">Anthropic</SelectItem>
                          <SelectItem value="Ollama">Ollama (Local Model)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-[12.5px] font-bold text-slate-800">Model</Label>
                      <Select value={draft.model} onValueChange={(v) => setDraft({ ...draft, model: v })}>
                        <SelectTrigger className="mt-1 h-9 text-[12.5px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gpt-4o">gpt-4o (High Reasoning)</SelectItem>
                          <SelectItem value="gpt-4o-mini">gpt-4o-mini (Lightweight / Fast)</SelectItem>
                          <SelectItem value="claude-3-5-sonnet">claude-3-5-sonnet</SelectItem>
                          <SelectItem value="llama3:latest">llama3 (Local Ollama)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-[12.5px] font-semibold text-slate-800">
                      <span>Temperature: {draft.temperature}</span>
                      <span className="text-[11px] text-slate-500">Focused → Creative</span>
                    </div>
                    <Slider
                      className="mt-2"
                      value={[draft.temperature]}
                      min={0}
                      max={1}
                      step={0.1}
                      onValueChange={([v]) => setDraft({ ...draft, temperature: v })}
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-100">
                    <div className="text-[13px] font-bold text-slate-900 mb-2">Isolated OpenClaw Memory Workspace</div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[12.5px] font-medium text-slate-800">Isolated Agent Memory Workspace</span>
                        <Switch
                          checked={Boolean(draft.memory_settings?.isolated_workspace ?? true)}
                          onCheckedChange={(v) => setDraft({
                            ...draft,
                            memory_settings: { ...draft.memory_settings, isolated_workspace: v },
                          })}
                        />
                      </div>
                      <p className="text-[11.5px] text-slate-500 leading-relaxed">
                        Ensures OpenClaw maintains an independent filesystem workspace and conversation state for this specific employee. Memory is never shared across agents.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 8: PERMISSIONS */}
              {wizardStep === 7 && (
                <div className="space-y-4">
                  <p className="text-[12px] text-slate-500">Configure action level permissions for this Digital Employee.</p>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                    {(catalog.permissions || []).map((p) => {
                      const isGranted = (draft.permissions || []).some((item) => item.permission_id === p.id || item.id === p.id || item.key === p.key);

                      return (
                        <div
                          key={p.id || p.key}
                          className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-[12.5px]"
                        >
                          <div>
                            <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                              <span>{p.label}</span>
                              <Badge className="bg-slate-100 text-slate-600 text-[9.5px] font-medium">
                                {p.group}
                              </Badge>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">{p.desc}</div>
                          </div>
                          <Switch
                            checked={isGranted}
                            onCheckedChange={(v) => {
                              const next = (draft.permissions || []).filter((item) => item.permission_id !== p.id && item.id !== p.id && item.key !== p.key);
                              if (v) {
                                next.push({ permission_id: p.id, id: p.id, key: p.key });
                              }
                              setDraft({ ...draft, permissions: next });
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* STEP 9: REVIEW & CREATE */}
              {wizardStep === 8 && (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-indigo-50/50 to-white p-5 space-y-4">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="text-[16px] font-bold text-slate-900">{draft.name}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12px]">
                      <div className="rounded-lg bg-white p-3 border border-slate-200">
                        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Responsibility</div>
                        <div className="text-slate-800 mt-0.5 leading-snug">{draft.primary_responsibility || draft.description || "Outbound prospecting & outreach"}</div>
                      </div>

                      <div className="rounded-lg bg-white p-3 border border-slate-200">
                        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Model</div>
                        <div className="text-slate-800 mt-0.5 font-mono">{draft.model} (Temp: {draft.temperature})</div>
                      </div>
                    </div>

                    <div className="space-y-2 text-[12px]">
                      <div>
                        <span className="font-semibold text-slate-900">Channels Assigned:</span>{" "}
                        <span className="text-slate-600">{draft.channels.length} connected channel account(s)</span>
                      </div>
                      <div>
                        <span className="font-semibold text-slate-900">Knowledge Base:</span>{" "}
                        <span className="text-slate-600">{draft.knowledge_source_ids.length} assigned source(s)</span>
                      </div>
                      <div>
                        <span className="font-semibold text-slate-900">Permitted Skills:</span>{" "}
                        <span className="text-slate-600">{draft.skills.length} skills enabled</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Stepper Footer Buttons */}
            <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                className="h-9 text-[12.5px]"
                disabled={wizardStep === 0 || saving}
                onClick={() => setWizardStep((s) => s - 1)}
              >
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>

              {wizardStep < WIZARD_STEPS.length - 1 ? (
                <Button
                  size="sm"
                  className="h-9 text-[12.5px] bg-indigo-600 hover:bg-indigo-700"
                  onClick={async () => {
                    if (await validateStepAsync(wizardStep)) {
                      setWizardStep((s) => s + 1);
                    }
                  }}
                >
                  Next Step <ChevronRight size={14} className="ml-1" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="h-9 px-5 text-[12.5px] font-semibold bg-indigo-600 hover:bg-indigo-700"
                  disabled={saving}
                  onClick={saveWizard}
                >
                  {saving ? (
                    <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</>
                  ) : editId ? (
                    "Save Digital Employee"
                  ) : (
                    "Create Digital Employee"
                  )}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* ========================================================================= */}
        {/* AGENT DETAIL / DASHBOARD MODAL */}
        {/* ========================================================================= */}
        {detailAgent && (
          <Dialog open={Boolean(detailAgent)} onOpenChange={() => setDetailAgent(null)}>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh] overflow-y-auto p-6 text-slate-900 shadow-xl border-slate-200">
              <DialogHeader className="pb-3 border-b border-slate-100 flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <DialogTitle className="text-[18px] font-bold text-slate-900">
                      {detailAgent.name}
                    </DialogTitle>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge className={cn("text-[10.5px]", statusBadge(detailAgent.status))}>
                        {detailAgent.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              {/* Tabs Nav */}
              <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto py-1 scrollbar-none">
                {["overview", "activity", "knowledge", "skills", "guardrails", "channels", "permissions"].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setDetailTab(tab)}
                    className={cn(
                      "px-3 py-1.5 text-[12px] font-semibold border-b-2 -mb-px capitalize transition-colors whitespace-nowrap",
                      detailTab === tab ? "text-indigo-700 border-indigo-600" : "text-slate-500 border-transparent hover:text-slate-800",
                    )}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="py-4 min-h-[300px]">
                {detailTab === "overview" && (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">System Instructions</div>
                      <p className="text-[12.5px] font-mono text-slate-800 mt-1 whitespace-pre-wrap leading-relaxed">
                        {detailAgent.system_prompt || "No custom instructions configured."}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
                      <div className="rounded-lg bg-white ring-1 ring-slate-200 p-3">
                        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Model</div>
                        <div className="font-mono text-slate-800 mt-0.5">{detailAgent.model || "gpt-4o"}</div>
                      </div>
                      <div className="rounded-lg bg-white ring-1 ring-slate-200 p-3">
                        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Temperature</div>
                        <div className="font-mono text-slate-800 mt-0.5">{detailAgent.temperature ?? 0.4}</div>
                      </div>
                      <div className="rounded-lg bg-white ring-1 ring-slate-200 p-3">
                        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">KB Sources</div>
                        <div className="font-semibold text-indigo-700 mt-0.5">{(detailAgent.knowledge || []).length} assigned</div>
                      </div>
                      <div className="rounded-lg bg-white ring-1 ring-slate-200 p-3">
                        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Channels</div>
                        <div className="font-semibold text-emerald-700 mt-0.5">{(detailAgent.channels || []).length} connected</div>
                      </div>
                    </div>
                  </div>
                )}

                {detailTab === "activity" && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-[13px] font-bold text-slate-900">Activity Log</div>
                      <Select value={activityTimeframe} onValueChange={(v) => setActivityTimeframe(v)}>
                        <SelectTrigger className="w-[120px] h-8 text-[12px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Today</SelectItem>
                          <SelectItem value="7">7 Days</SelectItem>
                          <SelectItem value="30">30 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {loadingActivity ? (
                      <div className="py-8 text-center text-[12.5px] text-slate-500"><Loader2 className="animate-spin inline mr-1" /> Loading activity…</div>
                    ) : !activityLogs.length ? (
                      <div className="py-8 text-center text-[12.5px] text-slate-500 border border-dashed rounded-xl">No recorded activity for this employee.</div>
                    ) : (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                        {activityLogs.map((log) => (
                          <div key={log.id} className="rounded-lg border border-slate-200 bg-white p-3 text-[12px] flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-slate-900">{log.event_type}</div>
                              <div className="text-[11px] text-slate-500 font-mono mt-0.5">{log.created_at}</div>
                            </div>
                            <Badge className="bg-slate-100 text-slate-600 text-[10px]">{log.channel || "system"}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {detailTab === "knowledge" && (
                  <div className="space-y-2">
                    <div className="text-[13px] font-bold text-slate-900 mb-2">Assigned Knowledge Sources</div>
                    {(detailAgent.knowledge || []).map((k) => (
                      <div key={k.assignment_id || k.source_id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-start justify-between gap-3 text-[12.5px] min-w-0 w-full">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 break-words whitespace-normal leading-snug" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                            {k.name || k.source_id}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 break-words whitespace-normal">
                            {k.source_type || "Document Source"}
                          </div>
                        </div>
                        <Badge className="bg-indigo-50 text-indigo-700 text-[10px] shrink-0 self-start mt-0.5">Isolated Agent KB</Badge>
                      </div>
                    ))}
                  </div>
                )}

                {detailTab === "skills" && (
                  <div className="space-y-2">
                    <div className="text-[13px] font-bold text-slate-900 mb-2">Permitted Execution Skills</div>
                    {(detailAgent.skills || []).map((s) => (
                      <div key={s.id || s.skill_key} className="rounded-xl border border-slate-200 bg-white p-3 text-[12.5px] flex items-center justify-between">
                        <span className="font-semibold text-slate-900 capitalize">{s.skill_key.replace(/_/g, " ")}</span>
                        <CheckCircle2 size={16} className="text-emerald-600" />
                      </div>
                    ))}
                  </div>
                )}

                {detailTab === "guardrails" && (
                  <div className="space-y-2">
                    <div className="text-[13px] font-bold text-slate-900 mb-2">Active Guardrail Rules</div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2 text-[12.5px]">
                      <div className="flex justify-between">
                        <span>Require Draft Approval:</span>
                        <span className="font-bold">{detailAgent.guardrails?.require_draft_approval ? "Yes" : "No"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Working Hours Only:</span>
                        <span className="font-bold">{detailAgent.guardrails?.working_hours_only ? "Yes" : "No"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>PII Redaction:</span>
                        <span className="font-bold">{detailAgent.guardrails?.pii_redaction ? "Yes" : "No"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Max Daily Outreach:</span>
                        <span className="font-bold">{detailAgent.guardrails?.max_daily_outreach || 50} messages</span>
                      </div>
                    </div>
                  </div>
                )}

                {detailTab === "channels" && (
                  <div className="space-y-2">
                    <div className="text-[13px] font-bold text-slate-900 mb-2">Connected Communication Accounts</div>
                    {(detailAgent.channels || []).map((ch) => (
                      <div key={ch.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between text-[12.5px]">
                        <div className="font-semibold text-slate-900 capitalize flex items-center gap-1.5">
                          <span>{ch.kind}</span>
                          <span className="text-[11px] text-slate-500 font-mono">({ch.account_identifier})</span>
                        </div>
                        <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">Connected</Badge>
                      </div>
                    ))}
                  </div>
                )}

                {detailTab === "permissions" && (
                  <div className="space-y-2">
                    <div className="text-[13px] font-bold text-slate-900 mb-2">Action Permissions</div>
                    {(detailAgent.permissions || []).map((p) => (
                      <div key={p.key || p.permission_key} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between text-[12.5px]">
                        <span className="font-semibold text-slate-900">{(p.key || p.permission_key).replace(/_/g, " ")}</span>
                        {p.granted ? <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">Granted</Badge> : <Badge className="bg-slate-100 text-slate-500 text-[10px]">Denied</Badge>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* ========================================================================= */}
        {/* INTERACTIVE TEST AGENT DRAWER */}
        {/* ========================================================================= */}
        {testAgent && (
          <Dialog open={Boolean(testAgent)} onOpenChange={() => setTestAgent(null)}>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-xl max-h-[85vh] overflow-y-auto p-5 text-slate-900 shadow-xl border-slate-200 flex flex-col justify-between">
              <div>
                <DialogHeader className="pb-2 border-b border-slate-100">
                  <DialogTitle className="text-[16px] font-bold text-slate-900">
                    Test {testAgent.name}
                  </DialogTitle>
                </DialogHeader>

                <div className="py-3 space-y-3 max-h-[360px] overflow-y-auto">
                  {!testMessages.length ? (
                    <div className="p-6 text-center text-[12.5px] text-slate-500 italic">
                      Send a test message to preview how this Digital Employee responds using its assigned instructions, knowledge, and skills.
                    </div>
                  ) : (
                    testMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          "p-3 rounded-xl text-[12.5px] leading-relaxed max-w-[85%]",
                          msg.role === "user"
                            ? "bg-indigo-600 text-white ml-auto"
                            : msg.isError
                            ? "bg-rose-50 text-rose-800 border border-rose-200"
                            : "bg-slate-100 text-slate-900 mr-auto",
                        )}
                      >
                        {msg.content}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center gap-2">
                <Input
                  className="h-9 text-[12.5px]"
                  placeholder="Type a test message to agent…"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !testing && testInput.trim()) {
                      e.preventDefault();
                      runTestMessage();
                    }
                  }}
                />
                <Button size="sm" className="h-9 px-3 bg-indigo-600" disabled={testing || !testInput.trim()} onClick={runTestMessage} aria-label="Send test message">
                  {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* ========================================================================= */}
        {/* DELETE CONFIRMATION DIALOG */}
        {/* ========================================================================= */}
        {deleteTarget && (
          <Dialog open={Boolean(deleteTarget)} onOpenChange={() => setDeleteTarget(null)}>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-[460px] p-5 text-slate-900 shadow-xl border-slate-200">
              <DialogHeader>
                <DialogTitle className="text-[17px] font-bold text-rose-900 flex items-center gap-2">
                  <AlertTriangle size={18} className="text-rose-600" /> Delete Digital Employee?
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3 py-2">
                <p className="text-[12.5px] text-slate-700 leading-relaxed">
                  Are you sure you want to delete <span className="font-bold text-slate-900">{deleteTarget.name}</span>?
                </p>

                <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 p-3 text-[12px] text-rose-900 space-y-1">
                  <div className="font-bold">This action will remove:</div>
                  <ul className="list-disc list-inside space-y-0.5 text-[11.5px]">
                    <li>Agent identity and system instructions</li>
                    <li>Channel account assignments</li>
                    <li>Isolated OpenClaw memory workspace</li>
                    <li>Custom skill and guardrail configurations</li>
                  </ul>
                  <div className="pt-1 text-[11px] text-rose-700 italic">
                    Shared Brain knowledge sources and global channel connections will remain preserved.
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <Button variant="outline" size="sm" className="h-9 text-[12.5px]" onClick={() => setDeleteTarget(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-9 text-[12.5px] bg-rose-600 hover:bg-rose-700 font-semibold"
                  disabled={deleting}
                  onClick={confirmDelete}
                >
                  {deleting ? "Deleting…" : "Yes, Delete Agent"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

      </div>
    </Shell>
  );
}
